/* eslint-disable lyrahealth/no-react-native-text */
import React, { ReactElement } from 'react'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { addDays, format } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  FieldSchema,
  FieldValue,
  UiMetadata,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import FormItemPDF from './FormItemPDF'
import ItemListPDF from './ItemListPDF'
import { logToSentry } from '../../../../common/utils/utils'
import { getGeneralNoteTitle } from '../../../clientNotes/utils/noteTitleUtils'

type GetNoteExternalValues = (note: Note, session?: Appointment) => { [key: string]: FieldValue } | undefined

type AppointmentInfo = { appointmentId: number; appointmentTimeslot: string; sessionNumber?: number }

type ClientNotesPDFProps = {
  isDAEngageProvider: boolean
  appointmentsInfoForPDF: AppointmentInfo[]
  notes: Note[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
  sessions: Appointment[]
  userId: string
  enableExportClientRecordsLogging: boolean
  getNoteExternalValues?: GetNoteExternalValues
}

const styles = StyleSheet.create({
  container: {
    fontFamily: 'Helvetica',
  },
  sectionTitle: {
    fontSize: 18,
    color: lightThemeColors.textSecondary,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textSecondary,
    marginBottom: 20,
    paddingBottom: 5,
  },
  noteContainer: {
    marginBottom: 30,
    padding: 16,
    border: `1px solid ${lightThemeColors.borderDefault}`,
    borderRadius: 8,
    backgroundColor: lightThemeColors.backgroundPrimary,
  },
  noteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${lightThemeColors.borderDefault}`,
  },
  noteTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: lightThemeColors.textPrimary,
  },
  noteStatus: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    textTransform: 'uppercase',
    backgroundColor: lightThemeColors.cardBackgroundSecondary,
    padding: '4px 8px',
    borderRadius: 4,
  },
  noteMetadata: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metadataItem: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
  },
  noteContent: {
    marginTop: 8,
  },
  emptySection: {
    fontSize: 12,
    color: lightThemeColors.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
    marginBottom: 30,
  },
  overviewContainer: {
    marginBottom: 30,
  },
  overviewContainerHeader: {
    fontSize: 16,
    color: lightThemeColors.textPrimary,
    marginBottom: 12,
  },
  inProgressForm: {
    fontSize: 11,
    color: lightThemeColors.textSecondary,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: 16,
  },
})

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a')
  } catch {
    return dateString
  }
}

const getNoteTitle = ({ note, sessionNumber }: { note: any; sessionNumber?: number }): string => {
  if (note.body?.subject) {
    return note.body.subject as string
  }

  if (note.body?.noteTitle) {
    return note.body.noteTitle as string
  }

  switch (note.note_type) {
    case NoteTypes.SESSION:
      if (sessionNumber) {
        return `Session ${sessionNumber} Note`
      }
      return 'Session Note'
    case NoteTypes.INITIAL:
      return 'Initial Note'
    case NoteTypes.GENERAL:
      return getGeneralNoteTitle(note) || 'General Note'
    case NoteTypes.REFERRAL:
      return 'Care Referral Form'
    default:
      return note.note_type
  }
}

const getStatusDisplay = (status: string) => {
  switch (status) {
    case NoteStatusStates.COMPLETED:
      return 'Completed'
    case NoteStatusStates.DRAFT:
      return 'In Progress'
    default:
      return status
  }
}

const NoteItem: React.FC<{
  note: Note
  appointmentTimeslot?: string
  enableExportClientRecordsLogging: boolean
  sessionNumber?: number
  externalValues?: { [key: string]: FieldValue }
}> = ({ note, appointmentTimeslot, enableExportClientRecordsLogging, sessionNumber, externalValues }) => {
  const noteBody: { [key: string]: FieldValue } = note.body as { [key: string]: FieldValue }

  const formUISchema: UiMetadata | undefined = note.content_meta_data?.meta_data?.uiSchema
  const formSchema: FieldSchema | undefined = note.content_meta_data?.meta_data?.schema as FieldSchema
  const formUISchemaOrder: string[] = formUISchema?.['ui:order'] ?? Object.keys(noteBody)

  if (enableExportClientRecordsLogging) {
    logToSentry('recordsPDFExport', {
      message: 'RecordsPDFExport - NoteItem',
      location: 'ClientNotesPDF - NoteItem',
      episodeId: note.episode_id,
      note: {
        noteBody,
        formUISchema,
        formSchema,
        formUISchemaOrder,
        noteTitle: getNoteTitle({ note, sessionNumber }),
        noteStatus: note.status,
        noteDisplayStatus: getStatusDisplay(note.status),
        noteUpdateDate: note.update_date,
        formatedUpdateDate: formatDate(note.update_date),
        noteAppointmentId: note.appointment_id,
        appointmentTimeslot,
      },
    })
  }
  if (note.status === NoteStatusStates.DRAFT) {
    const statusMessage = noteBody && Object.keys(noteBody).length > 1 ? 'In Progress' : 'Not Started'
    return (
      <View style={styles.noteContainer}>
        <View style={styles.noteHeader}>
          <Text style={styles.noteTitle}>{getNoteTitle({ note, sessionNumber })}</Text>
          <Text style={styles.noteStatus}>{getStatusDisplay(note.status)}</Text>
        </View>

        <View style={styles.noteMetadata}>
          <Text style={styles.metadataItem}>Last Updated: {formatDate(note.update_date)}</Text>
          {noteBody?.authorName && <Text style={styles.metadataItem}>Author: {noteBody.authorName}</Text>}
        </View>
        {appointmentTimeslot && (
          <View style={styles.noteMetadata}>
            <Text style={styles.metadataItem}>Session Time: {appointmentTimeslot}</Text>
          </View>
        )}
        <Text style={styles.inProgressForm}>{statusMessage}</Text>
      </View>
    )
  }

  return (
    <View style={{ ...styles.noteContainer, minHeight: '100px' }}>
      <View style={styles.noteHeader}>
        <Text style={styles.noteTitle}>{getNoteTitle({ note, sessionNumber })}</Text>
        <Text style={styles.noteStatus}>{getStatusDisplay(note.status)}</Text>
      </View>

      <View style={styles.noteMetadata}>
        <Text style={styles.metadataItem}>Last Updated: {formatDate(note.update_date)}</Text>
        {noteBody?.authorName && <Text style={styles.metadataItem}>Author: {noteBody.authorName}</Text>}
      </View>
      {appointmentTimeslot && (
        <View style={styles.noteMetadata}>
          <Text style={styles.metadataItem}>Session Time: {appointmentTimeslot}</Text>
        </View>
      )}

      <View style={styles.noteContent}>
        <FormItemPDF
          key={note.id}
          formBody={noteBody}
          formUISchema={formUISchema}
          formSchema={formSchema}
          formUISchemaOrder={formUISchemaOrder}
          externalValues={externalValues}
        />
      </View>
    </View>
  )
}

const NotesOverViewSubsection: React.FC<{
  title: string
  notes: Note[]
  appointmentsInfoForPDF?: AppointmentInfo[]
  enableExportClientRecordsLogging: boolean
}> = ({ title, notes, appointmentsInfoForPDF, enableExportClientRecordsLogging }) => {
  if (enableExportClientRecordsLogging) {
    logToSentry('recordsPDFExport', {
      message: 'RecordsPDFExport - NotesOverViewSubsection',
      title,
      location: 'ClientNotesPDF - NotesOverViewSubsection',
      episodeId: notes[0]?.episode_id,
      notes: notes.map((note) => {
        const appointmentInfo: AppointmentInfo | undefined = appointmentsInfoForPDF?.find(
          (timeslot) => timeslot.appointmentId === note.appointment_id,
        )
        const appointmentTimeslot = appointmentInfo?.appointmentTimeslot
        const sessionNumber = appointmentInfo?.sessionNumber
        return {
          title: getNoteTitle({ note, sessionNumber }),
          details: [
            appointmentTimeslot ? appointmentTimeslot : '',
            `Note Last Updated on ${formatDate(note.update_date)}`,
            note.body?.authorName ? `Submitted by ${note.body.authorName}` : '',
          ].filter(Boolean),
          status: note.status,
          displayStatus: getStatusDisplay(note.status),
        }
      }),
    })
  }
  return (
    <View>
      {notes.length > 0 && (
        <View style={styles.overviewContainer}>
          <Text style={styles.overviewContainerHeader}>{title}</Text>
          <ItemListPDF
            items={notes.map((note) => {
              const appointmentInfo: AppointmentInfo | undefined = appointmentsInfoForPDF?.find(
                (timeslot) => timeslot.appointmentId === note.appointment_id,
              )
              const appointmentTimeslot = appointmentInfo?.appointmentTimeslot
              const sessionNumber = appointmentInfo?.sessionNumber
              return {
                title: getNoteTitle({ note, sessionNumber }),
                details: [
                  appointmentTimeslot ? appointmentTimeslot : '',
                  `Note Last Updated on ${formatDate(note.update_date)}`,
                  note.body?.authorName ? `Submitted by ${note.body.authorName}` : '',
                ].filter(Boolean),
                rightItem: <Text style={styles.noteStatus}>{getStatusDisplay(note.status)}</Text>,
              }
            })}
          />
        </View>
      )}
    </View>
  )
}

// Component to render notes overview table
const NotesOverview: React.FC<{
  appointmentsInfoForPDF?: AppointmentInfo[]
  filteredSessionNotes: Note[]
  filteredGeneralNotes: Note[]
  filteredReferralNotes: Note[]
  enableExportClientRecordsLogging: boolean
}> = ({
  appointmentsInfoForPDF,
  filteredSessionNotes,
  filteredGeneralNotes,
  filteredReferralNotes,
  enableExportClientRecordsLogging,
}) => {
  if (enableExportClientRecordsLogging) {
    logToSentry('recordsPDFExport', {
      message: 'RecordsPDFExport - NotesOverview',
      location: 'ClientNotesPDF - NotesOverview',
      episodeId: filteredSessionNotes[0]?.episode_id,
      filteredSessionNotes: filteredSessionNotes.map((note) => {
        return {
          noteId: note.id,
          noteType: note.note_type,
          appointmentId: note.appointment_id,
          noteEpisodeId: note.episode_id,
        }
      }),
    })
  }
  return (
    <View>
      <Text style={styles.sectionTitle}>Notes Overview</Text>
      {filteredSessionNotes.length > 0 && (
        <NotesOverViewSubsection
          title='Session Notes'
          appointmentsInfoForPDF={appointmentsInfoForPDF}
          notes={filteredSessionNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
      {filteredGeneralNotes.length > 0 && (
        <NotesOverViewSubsection
          title='General Notes'
          notes={filteredGeneralNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
      {filteredReferralNotes.length > 0 && (
        <NotesOverViewSubsection
          title='Care Referral Forms'
          notes={filteredReferralNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
    </View>
  )
}

const NotesSection: React.FC<{
  appointmentsInfoForPDF?: AppointmentInfo[]
  title: string
  filteredNotes: Note[]
  enableExportClientRecordsLogging: boolean
  sessions?: Appointment[]
  getNoteExternalValues?: GetNoteExternalValues
}> = ({
  appointmentsInfoForPDF,
  title,
  filteredNotes,
  enableExportClientRecordsLogging,
  sessions,
  getNoteExternalValues,
}) => {
  if (enableExportClientRecordsLogging) {
    logToSentry('recordsPDFExport', {
      message: 'RecordsPDFExport - NotesSection',
      title,
      location: 'ClientNotesPDF - NotesSection',
      episodeId: filteredNotes[0]?.episode_id,
      filteredNotes: filteredNotes
        .sort((a, b) => new Date(b.update_date).getTime() - new Date(a.update_date).getTime())
        .map((note) => {
          const appointmentTimeslot: string | undefined = appointmentsInfoForPDF?.find(
            (timeslot) => timeslot.appointmentId === note.appointment_id,
          )?.appointmentTimeslot
          return {
            noteId: note.id,
            noteType: note.note_type,
            appointmentId: note.appointment_id,
            appointmentTimeslot,
          }
        }),
    })
  }

  return (
    <View>
      <Text style={styles.sectionTitle}>{title}</Text>
      {filteredNotes
        .sort((a, b) => new Date(b.update_date).getTime() - new Date(a.update_date).getTime())
        .map((note, index) => {
          const appointmentInfo: AppointmentInfo | undefined = appointmentsInfoForPDF?.find(
            (timeslot) => timeslot.appointmentId === note.appointment_id,
          )
          const appointmentTimeslot = appointmentInfo?.appointmentTimeslot
          const sessionNumber = appointmentInfo?.sessionNumber
          return (
            <NoteItem
              key={note.id || index}
              note={note}
              appointmentTimeslot={appointmentTimeslot}
              enableExportClientRecordsLogging={enableExportClientRecordsLogging}
              sessionNumber={sessionNumber}
              externalValues={getNoteExternalValues?.(
                note,
                sessions?.find((session) => session.appointmentId === note.appointment_id),
              )}
            />
          )
        })}
    </View>
  )
}

const ClientNotesPDF = ({
  isDAEngageProvider,
  appointmentsInfoForPDF,
  notes,
  pdfContentStartDate,
  pdfContentEndDate,
  sessions,
  userId,
  enableExportClientRecordsLogging,
  getNoteExternalValues,
}: ClientNotesPDFProps): ReactElement<any, any> | undefined => {
  const filteredNotes =
    pdfContentStartDate || pdfContentEndDate
      ? notes.filter((note) => {
          const isSessionNote = note.note_type === NoteTypes.SESSION || note.note_type === NoteTypes.INITIAL
          const noteSession = isSessionNote
            ? sessions.find((session) => session.appointmentId === note.appointment_id)
            : null
          const noteDate = noteSession ? getDateFromAppointment(noteSession) : new Date(note.update_date)
          return (
            (pdfContentStartDate ? noteDate >= new Date(pdfContentStartDate) : true) &&
            (pdfContentEndDate ? noteDate <= addDays(new Date(pdfContentEndDate), 1) : true)
          )
        })
      : notes

  if (enableExportClientRecordsLogging) {
    logToSentry('recordsPDFExport', {
      message: 'RecordsPDFExport - ClientNotesPDF',
      userId,
      pdfContentStartDate,
      pdfContentEndDate,
      location: 'ClientNotesPDF - ClientNotesPDF',
      notesCount: notes.length,
      episodeID: notes[0]?.episode_id,
      notes: notes?.map((note) => {
        return {
          noteId: note.id,
          noteType: note.note_type,
          noteProviderId: note.provider_id,
          appointmentId: note.appointment_id,
          noteEpisodeId: note.episode_id,
        }
      }),
      filteredNotes: filteredNotes?.map((note) => {
        return {
          noteId: note.id,
          noteType: note.note_type,
          noteProviderId: note.provider_id,
          appointmentId: note.appointment_id,
          noteEpisodeId: note.episode_id,
        }
      }),
      sessionsCount: sessions.length,
      appointmentsInfoForPDF: appointmentsInfoForPDF,
    })
  }

  const filteredSessionNotes = filteredNotes.filter(
    (note) => note.note_type === NoteTypes.SESSION || note.note_type === NoteTypes.INITIAL,
  )
  const filteredGeneralNotes = filteredNotes.filter((note) => note.note_type === NoteTypes.GENERAL)
  const filteredReferralNotes = filteredNotes.filter((note) => note.note_type === NoteTypes.REFERRAL)

  if (!notes || notes.length === 0) {
    return (
      <View>
        <Text style={styles.sectionTitle}>Notes Overview</Text>
        <Text style={styles.emptySection}>No notes yet</Text>
      </View>
    )
  }

  if (!filteredNotes || filteredNotes.length === 0) {
    return (
      <View>
        <Text style={styles.sectionTitle}>Notes Overview</Text>
        <Text style={styles.emptySection}>No notes for selected date range yet</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      {!pdfContentStartDate && !pdfContentEndDate && (
        <NotesOverview
          appointmentsInfoForPDF={appointmentsInfoForPDF}
          filteredSessionNotes={filteredSessionNotes}
          filteredGeneralNotes={filteredGeneralNotes}
          filteredReferralNotes={filteredReferralNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
      {filteredSessionNotes.length > 0 && (
        <NotesSection
          title='Session Notes'
          appointmentsInfoForPDF={appointmentsInfoForPDF}
          filteredNotes={filteredSessionNotes}
          sessions={sessions}
          getNoteExternalValues={getNoteExternalValues}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
      {filteredGeneralNotes.length > 0 && (
        <NotesSection
          title='General Notes'
          filteredNotes={filteredGeneralNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
      {filteredReferralNotes.length > 0 && !isDAEngageProvider && (
        <NotesSection
          title='Care Referral Forms'
          filteredNotes={filteredReferralNotes}
          enableExportClientRecordsLogging={enableExportClientRecordsLogging}
        />
      )}
    </View>
  )
}

export default ClientNotesPDF
